import { of, throwError as observableThrowError } from "rxjs";

import { catchError, map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { HttpClient } from "../../shared/services/http/http-client.service";
import {
  DriverViewModel,
  DriverNote,
  DriverNoteEmail,
} from "@app/shared/models/drivers/DriverViewModel.model";
import {
  DriverEventViewModel,
  DriverEventChangeViewModel,
} from "@app/shared/models/drivers/DriverEventViewModel.model";
import {
  DriverPeopleNetSummaryModel,
  DriverLocationModel,
  DriverMilesSummaryModel,
  DriverHoursOfServiceModel,
} from "@app/shared/models/drivers/DriverPeopleNetSummary.model";

@Injectable({ providedIn: "root" })
export class DriverService {
  url: String;
  constructor(private _http: HttpClient) {}

  getDriverList(includeInactive: boolean, includeTeams = false) {
    return this._http.post("api/driver/list", { includeInactive, includeTeams }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: any) => {
        return observableThrowError(error.message || "Server error");
      })
    );
  }

  getDriverListGrid() {
    return this._http.post("api/driver/driverListGrid", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => {
        return observableThrowError(error.message || "Server error");
      })
    );
  }

  getTeamlessDrivers() {
    return this._http.get("api/driver/listWithoutTeams").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  findAndFilter() {
    return this._http.post("api/driver/findAndFilter", {}).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDriverGridPaginated(
    filter: any,
    pageSize: number,
    pageOffset: number,
    sortField: string,
    sortOrder: number
  ) {
    return this._http
      .post("api/driver/driverListPaginated", {
        filter,
        pageSize,
        pageOffset,
        sortField,
        sortOrder,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  getExportDrivers(driverIds: string[], filter: any, selectAll: boolean) {
    return this._http
      .post("api/driver/exportDrivers", {
        driverIds,
        filter,
        selectAll,
      })
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  updateDriver(driver: DriverViewModel) {
    return this._http.patch("api/driver/update", driver).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.error ?? error.message ?? "Server error")
      )
    );
  }

  updateSpecialNote(driverId: string, specialNote: string | null) {
    return this._http
      .patch("api/driver/updateSpecialNote", { driverId, specialNote })
      .pipe(
        catchError((error: any) =>
          observableThrowError(error.error ?? error.message ?? "Server error")
        )
      );
  }

  updateDriverNo(driver: { id: string; idSystem: string }) {
    return this._http.patch("api/driver/updateDriverNo", driver).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDriverInfoById(id: string | number) {
    return this._http.get("api/driver/find/" + id).pipe(
      map((res: DriverViewModel) => res),
      catchError((error: any) => observableThrowError(error.message || "Server driverInfo error"))
    );
  }

  getDriverInfoByIdAsync(id: string | number): Promise<any> {
    return this._http.get("api/driver/find/" + id).toPromise();
  }

  /**
   * Default recipients for an email to a customer: the contact emails of the driver's scheduling team,
   * semicolon separated (empty when the team has no contacts with an email address).
   */
  getClientEmailRecipients(
    driver: { schedulingTeamContactEmails?: string[] } | null | undefined,
    driverId?: string | null
  ) {
    const known = DriverService.joinEmails(driver?.schedulingTeamContactEmails);
    if (known) return of(known);
    if (!driverId) return of("");

    return this.getDriverInfoById(driverId).pipe(
      map((fullDriver: DriverViewModel) =>
        DriverService.joinEmails(fullDriver?.schedulingTeamContactEmails)
      ),
      catchError(() => of(""))
    );
  }

  /** Joins email addresses with semicolons, dropping blanks and duplicates. */
  private static joinEmails(emails?: string[] | null): string {
    const unique = Array.from(
      new Set((emails || []).map((e) => (e || "").trim()).filter((e) => !!e))
    );
    return unique.join("; ");
  }

  listSelectFormat(includeInactive: boolean, includeTeams = false) {
    return this._http.post("api/driver/list", { includeInactive, includeTeams }).pipe(
      map((res) => {
        const drivers = res as any;
        drivers.map((driver) => (driver.itemName = `${driver.lastName}, ${driver.name}`));
        return drivers;
      }),
      catchError((error: any) => {
        return observableThrowError(error.message || "Server product creation error");
      })
    );
  }

  createNewDriver(driver: DriverViewModel) {
    return this._http.post("api/driver/create", driver).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.error ?? error.message ?? "Server error")
      )
    );
  }

  // Gets the last used trailer and product for the driver, and the driver's assigned tractorGets the last used trailer and product for the driver, and the driver's assigned tractor
  getDefaultSetup(driverId: string | number) {
    return this._http.post("api/driver/getDefaultSetup", { driverId }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server error las trailer used cant be created")
      )
    );
  }

  getDriversNoTractorAssigned() {
    return this._http.get("api/driver/getDriversWithNoTruck").pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server driverInfo error"))
    );
  }

  getBatchDriversDropdown(batchNo: number) {
    return this._http.get(`api/driver/listBatchDropDown?batchNo=${batchNo}`).pipe(
      map((drivers: any) => {
        return drivers.map((driver: DriverViewModel) => {
          return {
            label: `${driver.lastName}, ${driver.name}`,
            value: driver.id,
          };
        });
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getActiveBatchDriversDropdown() {
    return this._http.get(`api/driver/listActiveBatchDropDown`).pipe(
      map((drivers: any) => {
        return drivers.map((driver: DriverViewModel) => {
          return {
            label: `${driver.lastName}, ${driver.name}`,
            value: driver.id,
          };
        });
      }),
      catchError((error: any) =>
        observableThrowError(error.message || "Server trip creation error")
      )
    );
  }

  getCurrentBatchStatus(driverId: string) {
    return this._http.post("api/driver/currentBatchStatus", { driverId }).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getNotes(driverId: string) {
    return this._http.get(`api/driver/driverNotes/${driverId}`).pipe(
      map((res) => {
        return res as DriverNote[];
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDriverNote(noteId: string) {
    return this._http.get(`api/driver/notes/${noteId}`).pipe(
      map((res) => {
        return res as DriverNote;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  /** Returns the emails previously sent for a note, newest first. */
  getDriverNoteEmails(noteId: string, emailType?: string) {
    const params = emailType ? `?emailType=${encodeURIComponent(emailType)}` : "";
    return this._http.get(`api/driver/notes/${noteId}/emails${params}`).pipe(
      map((res) => {
        return res as DriverNoteEmail[];
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getClientDriverNotes(clientId: string) {
    return this._http.get(`api/driver/notes/by-client/${clientId}`).pipe(
      map((res) => {
        return res as DriverNote[];
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  saveNote(note: DriverNote) {
    return this._http.post("api/driver/notes/save", note).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  /** Creates a breakdown update, or edits it when an id is supplied. */
  saveDriverNoteUpdate(update: {
    id?: string;
    driverNoteId: string;
    label?: string;
    internalNotes?: string;
    clientNotes?: string;
  }) {
    return this._http.post("api/driver/notes/updates", update).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  deleteDriverNoteUpdate(updateId: string) {
    return this._http.delete(`api/driver/notes/updates/${updateId}`).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  deleteNote(noteId: string) {
    return this._http.delete(`api/driver/notes/${noteId}`).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getCustomerTrainingNames() {
    return this._http.get("api/driver/customerTrainingNames").pipe(
      map((res) => {
        return res as string[];
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getWeatherSuggestions() {
    return this._http.get("api/driver/notes/weatherSuggestions").pipe(
      map((res) => {
        return res as string[];
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  /** Active internal users that can be tagged on a complaint note. */
  getTaggableUsers() {
    return this._http.get("api/driver/notes/taggableUsers").pipe(
      map((res) => {
        return res as { id: string; name: string }[];
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getSchedulingTeamsByClient(clientId: string) {
    return this._http.get(`api/board/listByClient/${clientId}`).pipe(
      map((res) => res as any[]),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  sendCustomEmail(
    noteId: string,
    to: string,
    subject: string,
    body: string,
    emailType?: string,
    ccEmails?: string,
    attachments?: File[],
    driverNoteUpdateId?: string
  ) {
    const formData = new FormData();
    formData.append("noteId", noteId);
    formData.append("to", to);
    formData.append("subject", subject);
    formData.append("body", body);
    if (emailType) formData.append("emailType", emailType);
    formData.append("ccEmails", ccEmails ?? "");
    if (driverNoteUpdateId) formData.append("driverNoteUpdateId", driverNoteUpdateId);
    if (attachments) {
      attachments.forEach((file) => {
        formData.append("attachments", file, file.name);
      });
    }

    return this._http.post("api/driver/notes/sendCustomEmail", formData, null, true, true).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  // Driver Events API methods
  getDriverEvents(driverId: string) {
    return this._http.get(`api/driver/events/${driverId}`).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getRecentDutyStatuses(driverId: string) {
    return this._http.get(`api/peoplenet/listRecentDutyStatuses/${driverId}`).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDutyStatusesForDate(driverId: string, date: Date | string) {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    const dateStr = dateObj.toISOString().split("T")[0]; // Format as YYYY-MM-DD
    return this._http.get(`api/peoplenet/listDutyStatusesForDate/${driverId}/${dateStr}`).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDatesWithOnDutyStatus(driverId: string) {
    return this._http.get<Date[]>(`api/peoplenet/listDatesWithOnDutyStatus/${driverId}`).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  createDriverEvent(event: DriverEventViewModel) {
    return this._http.post("api/driver/events/create", event).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  updateDriverEvent(event: DriverEventViewModel) {
    return this._http.patch("api/driver/events/update", event).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  deleteDriverEvent(eventId: string) {
    return this._http.delete(`api/driver/events/delete/${eventId}`).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getEventChanges(eventId: string) {
    return this._http.get(`api/driver/events/changes/${eventId}`).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  createEventChange(change: DriverEventChangeViewModel) {
    return this._http.post("api/driver/events/changes/create", change).pipe(
      map((res) => {
        return res;
      }),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  // Schedule Upload methods
  previewScheduleUpload(request: any) {
    return this._http.post("api/driver/events/schedule/preview", request).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  confirmScheduleUpload(request: any) {
    return this._http.post("api/driver/events/schedule/confirm", request).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  undoScheduleUpload(batchId: string) {
    return this._http.delete(`api/driver/events/schedule/undo/${batchId}`).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  triggerAvatarSync() {
    return this._http.post(`api/avatar/sync`, {}).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  // PeopleNet API methods for driver location, miles, and hours of service
  getDriverLocation(driverId: string) {
    return this._http.get<DriverLocationModel>(`api/peoplenet/driverLocation/${driverId}`).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDriverMiles(driverId: string) {
    return this._http.get<DriverMilesSummaryModel>(`api/peoplenet/driverMiles/${driverId}`).pipe(
      map((res) => res),
      catchError((error: any) => observableThrowError(error.message || "Server error"))
    );
  }

  getDriverHoursOfService(driverId: string) {
    return this._http
      .get<DriverHoursOfServiceModel>(`api/peoplenet/driverHoursOfService/${driverId}`)
      .pipe(
        map((res) => res),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }

  getDriverSummary(driverId: string) {
    return this._http
      .get<DriverPeopleNetSummaryModel>(`api/peoplenet/driverSummary/${driverId}`)
      .pipe(
        map((res) => res),
        catchError((error: any) => observableThrowError(error.message || "Server error"))
      );
  }
}
